// "Your day, at a glance": every calendar chosen for it, merged and colour-coded (a dot, with the
// calendar's name on hover and focus), plus focus blocks and tasks due today in a neutral style,
// in the owner's zone, with what is happening now and what comes next ("in 25 min"). Free time is
// shown as free. No counts, no red, one encouraging line. Chips look at one calendar for now
// (remembered on this device). A right rail on the feed at wide widths; a compact section at the
// top of Tasks on narrower screens and phones, where the rest of the day folds away.
import { useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { CalendarDays, ChevronRight, Circle, Leaf, Settings2, Timer } from "lucide-react";
import { toast } from "sonner";
import {
  dayMoment,
  type AgendaItem,
  type AgendaView,
  type CalendarSource,
  type DayMoment,
} from "@shared/agenda";
import { isApiError } from "@web/lib/api";
import { cn } from "@web/lib/cn";
import { useAgenda } from "@web/lib/dayApi";
import { useNow } from "@web/lib/useNow";
import { Button } from "@web/ui/Button";
import { activeFilter, filterAgenda, readFilter, saveFilter, showCalendarChips } from "./dayFilter";
import { freeWords, NEXT_RIGHT_THING, rowWords } from "./dayWords";
import { removeFocusBlock } from "./focusOps";

type Variant = "rail" | "section";

function dateLine(view: AgendaView): string {
  try {
    return new Intl.DateTimeFormat("en-US", {
      weekday: "long",
      month: "long",
      day: "numeric",
      timeZone: view.timeZone,
    }).format(new Date(view.dayStart));
  } catch {
    return view.date;
  }
}

export function DayAtAGlance({ variant }: { variant: Variant }) {
  const agenda = useAgenda();
  const now = useNow(30_000);
  const view = agenda.data;
  const [remembered, setRemembered] = useState<string | null>(() => readFilter());
  const active = view ? activeFilter(remembered, view.calendars) : null;
  const items = useMemo(() => (view ? filterAgenda(view.items, active) : []), [view, active]);
  const moment = useMemo(() => (view ? dayMoment(items, now) : null), [view, items, now]);
  // Free time is about the whole day: a filter never makes a busy moment look free.
  const free = useMemo(() => (view ? dayMoment(view.items, now).free : null), [view, now]);
  const rail = variant === "rail";
  const choose = (key: string | null) => {
    setRemembered(key);
    saveFilter(key);
  };
  return (
    <section
      aria-label="Your day, at a glance"
      data-testid={`day-${variant}`}
      className={cn(
        "flex flex-col gap-3",
        rail ? "px-4 pt-4 pb-10" : "rounded-xl bg-paper px-4 py-3.5 ring-1 ring-line",
      )}
    >
      <header className="flex flex-col gap-0.5">
        <div className="flex items-center justify-between gap-2">
          <h2 className="mm-kicker flex items-center gap-1.5">
            <CalendarDays aria-hidden className="size-3.5 shrink-0" />
            Your day, at a glance
          </h2>
          <Link
            to="/settings/calendar"
            hash="your-day"
            aria-label="Choose which calendars show"
            title="Choose which calendars show"
            className="mm-tap rounded-md p-1 text-muted hover:bg-sunk hover:text-ink"
          >
            <Settings2 aria-hidden className="size-3.5" />
          </Link>
        </div>
        {view ? <p className="text-sm text-muted">{dateLine(view)}</p> : null}
      </header>
      {view && showCalendarChips(view.calendars) ? (
        <CalendarChips calendars={view.calendars} active={active} onChoose={choose} />
      ) : null}
      {agenda.isPending ? (
        <div aria-busy className="flex flex-col gap-2">
          <div className="h-3 w-2/3 rounded bg-sunk" />
          <div className="h-10 rounded-lg bg-sunk" />
        </div>
      ) : agenda.isError && !view ? (
        <p className="text-sm text-muted">
          Couldn’t load your day just now.{" "}
          <button
            type="button"
            className="text-accent-text underline underline-offset-2"
            onClick={() => void agenda.refetch()}
          >
            Try again
          </button>
        </p>
      ) : view && moment ? (
        <DayBody
          view={view}
          items={items}
          moment={moment}
          free={free}
          now={now}
          variant={variant}
        />
      ) : null}
    </section>
  );
}

/** "All", then one chip per calendar shown today: look at one for now. No counts. */
function CalendarChips({
  calendars,
  active,
  onChoose,
}: {
  calendars: readonly CalendarSource[];
  active: string | null;
  onChoose: (key: string | null) => void;
}) {
  const chip = (on: boolean) =>
    cn(
      "inline-flex h-7 items-center gap-1.5 rounded-full border px-2.5 text-xs",
      on ? "border-ink/30 bg-paper font-medium text-ink" : "border-line text-muted hover:text-ink",
    );
  return (
    <div role="group" aria-label="Show calendars" className="flex flex-wrap gap-1.5">
      <button
        type="button"
        aria-pressed={active === null}
        onClick={() => onChoose(null)}
        className={chip(active === null)}
      >
        All
      </button>
      {calendars.map((c) => (
        <button
          key={c.key}
          type="button"
          aria-pressed={active === c.key}
          onClick={() => onChoose(c.key)}
          className={chip(active === c.key)}
        >
          <Dot color={c.color} />
          {c.label}
        </button>
      ))}
    </div>
  );
}

function Dot({ color, className }: { color: number; className?: string }) {
  return (
    <span
      aria-hidden
      className={cn("inline-block size-2 shrink-0 rounded-full", className)}
      style={{ background: `var(--cal-${color})` }}
    />
  );
}

/** The calendar's name, shown on hover and focus (the dot alone says it at a glance). */
function SourceName({ source }: { source: CalendarSource | null }) {
  if (!source) return null;
  return (
    <span className="invisible text-xs text-muted group-focus-within:visible group-hover:visible">
      {source.label}
    </span>
  );
}

function DayBody({
  view,
  items,
  moment,
  free: freeAll,
  now,
  variant,
}: {
  view: AgendaView;
  items: readonly AgendaItem[];
  moment: DayMoment;
  /** Free time across every shown calendar, whatever the filter. */
  free: DayMoment["free"];
  now: number;
  variant: Variant;
}) {
  const [restOpen, setRestOpen] = useState(variant === "rail");
  const [earlierOpen, setEarlierOpen] = useState(false);
  const tz = view.timeZone;
  const allDay = items.filter((i) => !i.start && i.kind !== "task");
  const anyTime = items.filter((i) => !i.start && i.kind === "task");
  const shown = new Set<AgendaItem>([...moment.now, ...(moment.next ? [moment.next] : [])]);
  const later = items.filter((i) => i.start && !shown.has(i) && !moment.earlier.includes(i));
  const free = freeWords(freeAll, tz);
  const rest = later.length + anyTime.length + moment.earlier.length;

  return (
    <>
      {!view.calendar.connected ? (
        <p className="text-sm text-muted">
          Your calendar isn’t connected.{" "}
          <Link to="/settings/calendar" className="text-accent-text underline underline-offset-2">
            Connect Google Calendar
          </Link>{" "}
          to see your day here.
        </p>
      ) : !view.calendars.length ? (
        <p className="text-sm text-muted">
          No calendars are shown here right now.{" "}
          <Link
            to="/settings/calendar"
            hash="your-day"
            className="text-accent-text underline underline-offset-2"
          >
            Choose them in Settings
          </Link>
          .
        </p>
      ) : null}
      {allDay.length ? (
        <ul className="flex flex-wrap gap-1.5">
          {allDay.map((i) => (
            <li
              key={i.id}
              title={i.source ? `${i.title} · ${i.source.label}` : i.title}
              aria-label={i.source ? `All day: ${i.title}, ${i.source.label}` : undefined}
              className="inline-flex items-center gap-1.5 rounded-full border border-line bg-bg px-2.5 text-xs leading-6 text-ink-2"
            >
              {i.source ? <Dot color={i.source.color} /> : null}
              All day · {i.title}
            </li>
          ))}
        </ul>
      ) : null}

      <div className="flex flex-col gap-2">
        {moment.now.map((i) => (
          <Highlight key={i.id} item={i} moment={moment} now={now} tz={tz} />
        ))}
        {free && view.calendar.connected ? (
          <p
            className="flex items-center gap-2 rounded-lg border border-calm-line bg-calm-soft px-3 py-2 text-sm text-ink-2"
            data-testid="day-free"
          >
            <Leaf aria-hidden className="size-4 shrink-0 text-calm" />
            <span>
              <span className="font-medium text-ink">Now</span> · {free}
            </span>
          </p>
        ) : null}
        {moment.next ? <Highlight item={moment.next} moment={moment} now={now} tz={tz} /> : null}
      </div>

      {rest ? (
        <div className="flex flex-col gap-2">
          {variant === "section" ? (
            <button
              type="button"
              aria-expanded={restOpen}
              onClick={() => setRestOpen((o) => !o)}
              className="flex items-center gap-1.5 self-start rounded-md py-0.5 text-sm text-muted hover:text-ink"
            >
              <ChevronRight
                aria-hidden
                className={cn("size-4 transition-transform", restOpen && "rotate-90")}
              />
              The rest of today
            </button>
          ) : null}
          {restOpen ? (
            <>
              {later.length ? (
                <ul className="flex flex-col">
                  {later.map((i) => (
                    <Row key={i.id} item={i} moment={moment} now={now} tz={tz} />
                  ))}
                </ul>
              ) : null}
              {anyTime.length ? (
                <div>
                  <p className="mb-1 text-xs text-muted">Any time today</p>
                  <ul className="flex flex-col">
                    {anyTime.map((i) => (
                      <Row key={i.id} item={i} moment={moment} now={now} tz={tz} />
                    ))}
                  </ul>
                </div>
              ) : null}
              {moment.earlier.length ? (
                <div>
                  <button
                    type="button"
                    aria-expanded={earlierOpen}
                    onClick={() => setEarlierOpen((o) => !o)}
                    className="flex items-center gap-1.5 rounded-md py-0.5 text-xs text-muted hover:text-ink"
                  >
                    <ChevronRight
                      aria-hidden
                      className={cn("size-3.5 transition-transform", earlierOpen && "rotate-90")}
                    />
                    Earlier today
                  </button>
                  {earlierOpen ? (
                    <ul className="mt-1 flex flex-col opacity-70">
                      {moment.earlier.map((i) => (
                        <Row key={i.id} item={i} moment={moment} now={now} tz={tz} />
                      ))}
                    </ul>
                  ) : null}
                </div>
              ) : null}
            </>
          ) : null}
        </div>
      ) : null}

      {view.notes.map((n) => (
        <p key={n} className="text-xs text-muted">
          {n}
        </p>
      ))}
      <p className="font-title text-sm leading-snug text-ink-2 italic">{NEXT_RIGHT_THING}</p>
    </>
  );
}

const STATE_LABEL = { now: "Now", next: "Next" } as const;

/** Now and next: a little larger, with the relative time. */
function Highlight({
  item,
  moment,
  now,
  tz,
}: {
  item: AgendaItem;
  moment: DayMoment;
  now: number;
  tz: string;
}) {
  const words = rowWords(item, moment, now, tz);
  const label = words.state === "now" || words.state === "next" ? STATE_LABEL[words.state] : null;
  return (
    <div
      data-testid={`day-${words.state ?? "item"}`}
      className={cn(
        "group flex flex-col gap-0.5 rounded-lg border px-3 py-2",
        words.state === "now" ? "border-accent/40 bg-accent-soft/60" : "border-line bg-bg",
      )}
    >
      <p className="flex flex-wrap items-baseline gap-x-1.5 text-xs">
        {label ? <span className="font-semibold text-accent-text">{label}</span> : null}
        {words.detail ? <span className="text-muted">· {words.detail}</span> : null}
      </p>
      <ItemTitle item={item} className="font-medium" />
      <p className="flex flex-wrap items-center gap-x-1.5 text-xs text-muted tabular-nums">
        {words.when}
        <KindTag item={item} />
        <SourceName source={item.source} />
      </p>
      {item.kind === "focus" ? <FocusControls item={item} /> : null}
    </div>
  );
}

function Row({
  item,
  moment,
  now,
  tz,
}: {
  item: AgendaItem;
  moment: DayMoment;
  now: number;
  tz: string;
}) {
  const words = rowWords(item, moment, now, tz);
  return (
    <li className="group flex flex-col gap-0.5 border-t border-line py-2 first:border-t-0">
      <div className="flex items-baseline gap-2.5">
        <span className="w-[4.25rem] shrink-0 text-xs text-muted tabular-nums">{words.at}</span>
        <div className="min-w-0 flex-1">
          <ItemTitle item={item} className="text-sm" />
          {words.until || item.kind === "focus" || item.source ? (
            <p className="flex flex-wrap items-center gap-x-1.5 text-xs text-muted tabular-nums">
              {words.until}
              <KindTag item={item} />
              <SourceName source={item.source} />
            </p>
          ) : null}
        </div>
      </div>
      {item.kind === "focus" ? (
        <div className="pl-[4.875rem]">
          <FocusControls item={item} />
        </div>
      ) : null}
    </li>
  );
}

function ItemTitle({ item, className }: { item: AgendaItem; className?: string }) {
  const base = cn("block min-w-0 break-words text-ink", className);
  if (item.kind === "task" && item.task)
    return (
      <Link to="/tasks" search={{ task: item.task.taskId }} className={cn(base, "hover:underline")}>
        <Circle aria-hidden className="mr-1.5 inline size-3 align-[-1px] text-muted" />
        {item.title}
      </Link>
    );
  const dot = item.source ? <Dot color={item.source.color} className="mr-1.5 align-[1px]" /> : null;
  const named = item.source ? `${item.title}, ${item.source.label} calendar` : undefined;
  if (item.link)
    return (
      <a
        href={item.link}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={named}
        title={item.source?.label}
        className={cn(base, "hover:underline")}
      >
        {dot}
        {item.title}
      </a>
    );
  return (
    <span className={base} aria-label={named} title={item.source?.label}>
      {dot}
      {item.title}
    </span>
  );
}

function KindTag({ item }: { item: AgendaItem }) {
  if (item.kind !== "focus") return null;
  return (
    <span className="inline-flex items-center gap-1 text-xs text-calm">
      <Timer aria-hidden className="size-3" />
      {item.focus?.pending ? "Focus time · adding…" : "Focus time"}
    </span>
  );
}

/** Remove a block MeeMail made: arm first, then confirm (it deletes a calendar event). */
function FocusControls({ item }: { item: AgendaItem }) {
  const queryClient = useQueryClient();
  const [armed, setArmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const holdId = item.focus?.holdId;
  if (!holdId) return null;
  const remove = async () => {
    setBusy(true);
    try {
      await removeFocusBlock(queryClient, holdId);
      toast("Taking it off your calendar.");
    } catch (error) {
      toast(
        isApiError(error, "stale")
          ? "It’s already off your calendar."
          : "Couldn’t remove it just now. It’s still on your calendar.",
      );
    } finally {
      setBusy(false);
      setArmed(false);
    }
  };
  return armed ? (
    <div className="mt-1.5 flex flex-col gap-1.5" role="group" aria-label="Remove focus time">
      <p className="text-xs text-ink-2">Take it off your calendar?</p>
      <div className="flex gap-2">
        <Button size="sm" variant="quiet-danger" busy={busy} onClick={() => void remove()}>
          Remove
        </Button>
        <Button size="sm" variant="ghost" disabled={busy} onClick={() => setArmed(false)}>
          Keep
        </Button>
      </div>
    </div>
  ) : (
    <button
      type="button"
      onClick={() => setArmed(true)}
      className="mt-0.5 self-start text-xs text-muted underline underline-offset-2 hover:text-ink"
    >
      Remove
    </button>
  );
}
